import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Building2, Loader2, FileText, Share2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api, errMsg, fmtDate } from '../api';
import { PageHeader, Card, Empty, inputCls, goldBtn, ghostBtn } from '../ui';
import PurchaseDialog from '../PurchaseDialog';
import CompanySuggest from '../report/CompanySuggest';
import { COUNTRIES, CountryLabel } from '../countries';

const FOREIGN_FIELDS = [['name', 'Ragione sociale'], ['vatNo', 'Partita IVA / VAT'], ['regNo', 'Numero di registrazione'], ['city', 'Città'], ['postCode', 'Codice postale']];
const FIELDS = [['name', 'Ragione sociale'], ['vatNo', 'Partita IVA / C.F.'], ['regNo', 'Numero REA'], ['city', 'Città'], ['province', 'Provincia'], ['postCode', 'CAP']];

export default function SearchPage({ foreign = false, country = null, onNotFound = null }) {
  const countryList = foreign ? COUNTRIES.filter(([c]) => c !== 'IT') : COUNTRIES;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ countries: country || (foreign ? 'GB' : 'IT'), name: params.get('name') || '', vatNo: '', regNo: '', city: '', province: '', postCode: '', status: 'any' });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(null);
  const [disclaimers, setDisclaimers] = useState({});
  useEffect(() => { api.get('/cs/country-disclaimers').then((r) => setDisclaimers(r.data)).catch(() => {}); }, []);

  const run = async (e, override) => {
    e?.preventDefault();
    const q = Object.fromEntries(Object.entries(override || form).filter(([k, v]) => v && v !== 'any'));
    if (!q.name && !q.vatNo && !q.regNo) { toast.error('Inserisci almeno ragione sociale, partita IVA o numero REA'); return; }
    setBusy(true);
    try {
      const { data } = await api.get('/cs/search/companies', { params: { ...q, pageSize: 50 } });
      setResults(data);
    } catch (err) { toast.error(errMsg(err)); setResults({ companies: [], failed: true }); } finally { setBusy(false); }
  };

  useEffect(() => { if (params.get('name')) run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const pickSuggestion = (c) => {
    const next = { ...form, name: c.name, vatNo: c.vatNo || '' };
    setForm(next);
    run(null, c.vatNo ? { countries: form.countries, vatNo: c.vatNo } : next);
  };

  return (
    <div>
      {!foreign && <PageHeader kicker="Ricerca · gratuita e illimitata" title="Ricerca aziende" />}
      <Card className="p-6 mb-8 portal-rise">
        <form onSubmit={run} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {!country && (
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87] mb-2">Nazione{disclaimers[form.countries] && <Info size={12} className="inline ml-2 text-[#D4AF37] align-[-1px]" title={disclaimers[form.countries]} data-testid="search-country-disclaimer-icon" />}</label>
            <Select value={form.countries} onValueChange={set('countries')}>
              <SelectTrigger className={inputCls} data-testid="search-country"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0B0D10] border-[#1F242C] text-[#E5E5E5]">
                {countryList.map(([c, l]) => <SelectItem key={c} value={c} data-testid={`search-country-${c}`}><CountryLabel code={c} name={l} /></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}
          {(foreign ? FOREIGN_FIELDS : FIELDS).map(([k, l]) => (
            <div key={k}>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87] mb-2">{l}</label>
              {k === 'name'
                ? <CompanySuggest value={form.name} onChange={set('name')} country={form.countries} onPick={pickSuggestion} />
                : <Input value={form[k]} onChange={set(k)} className={inputCls} data-testid={`search-${k}`} />}
            </div>
          ))}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87] mb-2">Stato</label>
            <Select value={form.status} onValueChange={set('status')}>
              <SelectTrigger className={inputCls} data-testid="search-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0B0D10] border-[#1F242C] text-[#E5E5E5]">
                <SelectItem value="any">Tutte</SelectItem><SelectItem value="active">Attive</SelectItem><SelectItem value="nonActive">Non attive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <Button type="submit" disabled={busy} className={`${goldBtn} h-11 px-8`} data-testid="search-submit">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Search size={16} className="mr-2" /> Cerca</>}
            </Button>
          </div>
        </form>
        {disclaimers[form.countries] && (
          <div className="mt-5 flex gap-3 border-l-2 border-[#D4AF37] bg-[#D4AF37]/[0.05] px-4 py-3 text-sm text-[#C9CCD1]" data-testid="search-country-disclaimer">
            <Info size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
            <p><span className="text-[#F2F2F2]"><CountryLabel code={form.countries} name={(COUNTRIES.find(([c]) => c === form.countries) || [])[1]} /></span> · <span className="whitespace-pre-line">{disclaimers[form.countries]}</span></p>
          </div>
        )}
      </Card>

      {results && (
        <Card className="portal-rise">
          <div className="px-6 py-4 border-b border-[#1F242C] text-xs text-[#7A7F87] font-mono flex flex-wrap justify-between gap-2" data-testid="search-results-count">
            <span>{results.totalSize ?? results.companies?.length ?? 0} risultati</span>
            {onNotFound && !results.failed && results.companies?.length > 0 && <button onClick={() => onNotFound(form.name)} className="font-sans text-[#D4AF37] hover:underline" data-testid="search-results-investigate">Non trovi l'azienda? Richiedi un'investigazione</button>}
          </div>
          {(!results.companies || results.companies.length === 0) ? (
            <Empty icon={Building2} title="Nessuna azienda trovata" text={onNotFound ? "L'azienda non è nella banca dati: puoi richiedere un'investigazione." : "Prova a modificare i criteri: per l'Italia la partita IVA restituisce quasi sempre un solo risultato."}>
              {onNotFound && !results.failed && <Button onClick={() => onNotFound(form.name)} className={`${goldBtn} h-10`} data-testid="search-not-found-investigate">Richiedi un'investigazione</Button>}
            </Empty>
          ) : (
            <div className="divide-y divide-[#1F242C]">
              {results.companies.map((c) => (
                <div key={c.id} className="p-6 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`search-result-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-medium text-[#F2F2F2] truncate">{c.name}</p>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${/^active$/i.test(c.status || '') ? 'border-[#2E9E6A]/50 text-[#5FC18F]' : 'border-[#3A3F47] text-[#7A7F87]'}`}>{/^active$/i.test(c.status || '') ? 'Attiva' : /^nonactive$/i.test(c.status || '') ? 'Non attiva' : c.status || 'n.d.'}</span>
                    </div>
                    <p className="text-sm text-[#8A8F97] truncate">{c.address?.simpleValue}</p>
                    <p className="text-xs text-[#5A5F67] font-mono mt-1">{c.id} · {(c.vatNo || [])[0] ? `P.IVA ${c.vatNo[0]}` : (c.regNo ? `Reg. ${c.regNo}` : 'P.IVA —')} · {c.officeType === 'headOffice' ? 'Sede legale' : c.officeType || ''} · bilancio {fmtDate(c.dateOfLatestAccounts)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.owned ? (
                      <Button onClick={() => navigate(`/portal/report/${c.owned.report_id}`)} className={`${goldBtn} h-10`} data-testid={`open-report-${c.id}`}><FileText size={15} className="mr-2" />Apri report</Button>
                    ) : (
                      <Button onClick={() => setBuying(c)} className={`${goldBtn} h-10`} data-testid={`buy-report-${c.id}`}><FileText size={15} className="mr-2" />Acquista report</Button>
                    )}
                    <Button onClick={() => navigate(`/portal/grafo?company=${c.id}`)} className={`${ghostBtn} h-10`} data-testid={`graph-${c.id}`}><Share2 size={15} className="mr-2" />Grafo</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <PurchaseDialog company={buying} open={!!buying} onClose={() => setBuying(null)}
        onDone={(doc) => { setBuying(null); navigate(`/portal/report/${doc.id}`); }} />
    </div>
  );
}
